import React, { useRef, useState } from "react";
import { Alert, Avatar, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { userService } from "../../services/userService";
import { User } from "../../types";
import { apiErrorMessage, avatarColor, initials, nameFromEmail } from "./taskUtils";

interface NameDialogProps {
    open: boolean;
    onClose: () => void;
    /** Whose name: yourself (PATCH /users/me) or, for the Owner, anyone. */
    user: Pick<User, "user_id" | "email" | "display_name"> | null;
    self: boolean;
    onSaved: (user: User) => void;
}

/** The name shown on tasks, avatars and alerts. */
export const NameDialog: React.FC<NameDialogProps> = ({ open, onClose, user, self, onSaved }) => {
    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const fallback = user ? nameFromEmail(user.email) : "";
    const preview = name.trim() || fallback;

    const save = async () => {
        if (!user) return;
        setSaving(true);
        setError("");
        try {
            const saved = self ? await userService.updateMyName(name) : await userService.setUserName(user.user_id, name);
            onSaved(saved);
            onClose();
        } catch (err) {
            setError(apiErrorMessage(err, "Couldn't save the name."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            slotProps={{
                transition: {
                    onEnter: () => {
                        setName(user?.display_name || "");
                        setError("");
                    },
                    onEntered: () => inputRef.current?.select(),
                },
            }}
        >
            <DialogTitle>{self ? "Your name" : "Name"}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {self ? "How the team sees you on tasks, comments and alerts." : `How ${user?.email} shows on tasks, comments and alerts.`}
                </Typography>
                <TextField
                    inputRef={inputRef}
                    fullWidth
                    label="Name"
                    placeholder={fallback}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            save();
                        }
                    }}
                    slotProps={{ htmlInput: { maxLength: 60 } }}
                    helperText={name.trim() ? " " : `Left blank, it shows as “${fallback}”.`}
                />
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: avatarColor(user?.user_id), fontSize: 13, fontWeight: 700 }}>{initials(preview)}</Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {preview}
                    </Typography>
                </Stack>
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={saving}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};
